import React, { useState } from 'react';
import { Image } from 'react-native';
import Svg, { Text as SvgText } from 'react-native-svg';

import { Box } from '@/components/lib/components';
import {
    PLAYER_AVATAR_CONFIG,
    PlayerAvatarData,
    PlayerAvatarVariant,
} from '@/components/player-avatar/player-avatar-config';
import { mapPlayerDataToAvatarData } from '@/components/player-avatar/player-avatar-utils';
import { CollegeBasketballShirt } from '@/components/player-profile/tiles/college-sports/CollegeBasketballShirt';
import { CollegeFootballShirt } from '@/components/player-profile/tiles/college-sports/CollegeFootballShirt';
import { EntryItemPick } from '@/feature/entries-pickem/components/EntriesList';
import { League } from '@/types/api.generated';
import { addResizeParamToURL } from '@/utils/cdn-utils';
import { getPlayerJerseyNumber, shouldShowShirtImage } from '@/utils/formatPlayerInfo';
import { isPlayerImgPlaceholder } from '@/utils/player';

type PlayerAvatarProps = {
    playerData: PlayerAvatarData | EntryItemPick;
    playerAvatarVariant: PlayerAvatarVariant;
};

export const PlayerAvatar = ({ playerData, playerAvatarVariant }: PlayerAvatarProps) => {
    const player = mapPlayerDataToAvatarData(playerData);
    const config = PLAYER_AVATAR_CONFIG[playerAvatarVariant];
    const { collegeSvgNumberContainerStyle, imageStyle } = config;
    const shirtSize = typeof config.shirtSize === 'function' ? config.shirtSize(player) : config.shirtSize;
    const { width, height } = shirtSize;

    const svgTextProps = typeof config.svgTextProps === 'function' ? config.svgTextProps(player) : config.svgTextProps;
    const collegeBasketballProps = config.collegeBasketballProps(player);
    const collegeFootballProps = config.collegeFootballProps(player);
    const collegeShirtStyleContainer =
        typeof config.collegeShirtStyleContainer === 'function'
            ? config.collegeShirtStyleContainer(player)
            : config.collegeShirtStyleContainer;

    const placeholder = config.renderPlaceholder?.() ?? null;

    const imageUri = player.icon;
    const league = player.league;
    const jerseyNumber = player.jerseyNumber;
    const [shouldShowPlaceholder, setShouldShowPlaceholder] = useState(false);
    const [source, setSource] = useState({ uri: addResizeParamToURL(imageUri, 128) });

    const playerNumber = getPlayerJerseyNumber(league, jerseyNumber);
    const isPlaceholderImage = isPlayerImgPlaceholder(imageUri) || shouldShowPlaceholder;

    const showCollegeShirt = shouldShowShirtImage(playerNumber, isPlaceholderImage);
    const isCBB = league === League.Cbb;

    const handleImageError = () => {
        if (source?.uri !== imageUri) {
            setSource({ uri: imageUri });
        } else {
            setShouldShowPlaceholder(true);
        }
    };

    if (showCollegeShirt) {
        return (
            <Box>
                <Box style={collegeShirtStyleContainer}>
                    {isCBB ? (
                        <CollegeBasketballShirt width={width} height={height} {...collegeBasketballProps} />
                    ) : (
                        <CollegeFootballShirt width={width} height={height} {...collegeFootballProps} />
                    )}
                </Box>

                <Svg style={collegeSvgNumberContainerStyle}>
                    <SvgText {...svgTextProps}>{playerNumber}</SvgText>
                </Svg>
            </Box>
        );
    }
    if (isPlaceholderImage) {
        return placeholder;
    }
    if (source) {
        return playerAvatarVariant === 'profileHeader' && config?.renderImage ? (
            config?.renderImage(player, handleImageError)
        ) : (
            <Image resizeMode="cover" source={source} style={imageStyle} onError={handleImageError} />
        );
    }

    return null;
};
